import { Button, Grid, P } from '@farewill/ui'
import { COLOR } from '@farewill/ui/tokens'
import MessageBox from 'components/message-box'
import { formatCurrency, hasDecimals } from 'lib/formatting/currency'
import { useLeadContext } from '../context'

export const PaymentTaken = ({
  amountPaidInPence,
  amountLeftToPayForConversionInPence,
  paymentType,
}: {
  amountPaidInPence: number
  amountLeftToPayForConversionInPence: number
  paymentType: 'in_full' | 'instalments'
}): React.ReactElement => {
  const { onSubmit } = useLeadContext()

  if (amountPaidInPence === 0) {
    return <></>
  }

  const customerHasPaidExactAmount = amountLeftToPayForConversionInPence === 0
  const customerHasUnderpaid = amountLeftToPayForConversionInPence > 0
  const customerHasOverpaid = amountLeftToPayForConversionInPence < 0
  const paymentIsInFull = paymentType === 'in_full'

  return (
    <Grid data-testid="payment-taken-message-box">
      <Grid.Item>
        {customerHasPaidExactAmount && (
          <MessageBox success>
            <P color={COLOR.BLACK} size="L">
              Customer has paid{' '}
              {paymentIsInFull ? 'in full' : 'the first instalment'}:{' '}
              {formatCurrency({
                value: amountPaidInPence,
                valueInPence: true,
                showPence: hasDecimals(amountPaidInPence / 100),
              })}
            </P>
            <P>No further payment needed{paymentIsInFull ? '' : ' today'}.</P>
            <P>
              Check the customer information is correct before you convert the
              lead.
            </P>
          </MessageBox>
        )}

        {!customerHasPaidExactAmount && (
          <MessageBox warning>
            {customerHasUnderpaid && (
              <>
                <P color={COLOR.BLACK} size="L">
                  Customer has{' '}
                  {formatCurrency({
                    value: amountLeftToPayForConversionInPence,
                    valueInPence: true,
                    showPence: hasDecimals(
                      amountLeftToPayForConversionInPence / 100
                    ),
                  })}{' '}
                  left to pay
                </P>
                <P>
                  Customer has paid:{' '}
                  {formatCurrency({
                    value: amountPaidInPence,
                    valueInPence: true,
                    showPence: hasDecimals(amountPaidInPence / 100),
                  })}
                </P>
                <P>
                  Explain that you must take the payment in a single
                  transaction.
                  <br />
                  Refund the existing payment of{' '}
                  {formatCurrency({
                    value: amountPaidInPence,
                    valueInPence: true,
                    showPence: hasDecimals(amountPaidInPence / 100),
                  })}{' '}
                  before taking a new payment of{' '}
                  {formatCurrency({
                    value:
                      amountPaidInPence + amountLeftToPayForConversionInPence,
                    valueInPence: true,
                    showPence: hasDecimals(
                      (amountPaidInPence +
                        amountLeftToPayForConversionInPence) /
                        100
                    ),
                  })}
                  .
                </P>
              </>
            )}
            {customerHasOverpaid && (
              <>
                <P color={COLOR.BLACK} size="L">
                  Customer has overpaid by{' '}
                  {formatCurrency({
                    value: Math.abs(amountLeftToPayForConversionInPence),
                    valueInPence: true,
                    showPence: hasDecimals(
                      amountLeftToPayForConversionInPence / 100
                    ),
                  })}
                </P>
                <P>
                  Total amount paid:{' '}
                  {formatCurrency({
                    value: amountPaidInPence,
                    valueInPence: true,
                    showPence: hasDecimals(amountPaidInPence / 100),
                  })}
                </P>
                <P>
                  Refund the customer{' '}
                  {formatCurrency({
                    value: Math.abs(amountLeftToPayForConversionInPence),
                    valueInPence: true,
                    showPence: hasDecimals(
                      amountLeftToPayForConversionInPence / 100
                    ),
                  })}{' '}
                  in Stripe before you convert the lead.
                </P>
              </>
            )}
            <P>Need help? Contact Engineering.</P>
            {/* Causes the lead to refetch and allows Backstage user to get updated customer payment details  */}
            <Button.Secondary onClick={() => onSubmit}>
              I have refunded the customer
            </Button.Secondary>
          </MessageBox>
        )}
      </Grid.Item>
    </Grid>
  )
}
